import { Button, Modal, TextInput } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./file-sharing.scss";
import { baseFilename, collectFileLeafNodes, SAFE_FILENAME_PATTERN } from "./fileTreeUtils";
import { useAbortControllers } from "./useAbortControllers";
import { FolderNode } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    node: FolderNode;
    isOpen: boolean;
    onClose: () => void;
    onRenamed: () => void;
}

const RenameFolderModal = ({ node, isOpen, onClose, onRenamed }: Props): JSX.Element => {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();

    const [newName, setNewName] = React.useState(node.filename);
    const [validationError, setValidationError] = React.useState<string | undefined>();
    const [renameError, setRenameError] = React.useState<string | undefined>();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setNewName(node.filename);
            setValidationError(undefined);
            setRenameError(undefined);
        }
    }, [isOpen, node.filename]);

    const handleSubmit = React.useCallback(async () => {
        const trimmed = newName.trim();
        if (!trimmed || !SAFE_FILENAME_PATTERN.test(trimmed)) {
            setValidationError(t("FileSharing.renameFolder.errorInvalidName"));
            return;
        }

        const oldFolderPath = node.folderPath;
        const newFolderPath = node.directoryPath ? `${node.directoryPath}/${trimmed}` : trimmed;
        const leafNodes = collectFileLeafNodes(node);

        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.RENAME_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setLoading(true);
        setRenameError(undefined);

        // Each file is moved independently. Errors are accumulated so the user can see how many succeeded.
        let failedCount = 0;
        for (const file of leafNodes) {
            if (abortController.signal.aborted) {
                return;
            }
            const newPath = newFolderPath + file.directoryPath.slice(oldFolderPath.length);
            const filename = baseFilename(file.filename, file.directoryPath);
            try {
                await fileSharingService.renameFile(
                    { uuid: file.uuid, new_path: newPath, new_filename: filename, allow_override: false },
                    abortController
                );
            } catch (error) {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error(
                    "Failed to move file during folder rename",
                    error instanceof Error ? error : new Error(String(error))
                );
                failedCount++;
            }
        }

        if (!abortController.signal.aborted) {
            setLoading(false);
            if (failedCount > 0) {
                setRenameError(
                    failedCount === leafNodes.length
                        ? t("FileSharing.renameFolder.errorMessage")
                        : t("FileSharing.renameFolder.partialError", { failedCount, total: leafNodes.length })
                );
            } else {
                onRenamed();
                onClose();
            }
        }
    }, [node, newName, onRenamed, onClose, t]);

    return (
        <Modal
            isOpen={isOpen}
            hideModal={onClose}
            title={t("FileSharing.renameFolder.title")}
            className={style.dialog}
            buttons={
                <>
                    <Button
                        variant="Secondary"
                        onClick={onClose}
                        disabled={loading}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.cancel")}
                    </Button>
                    <Button
                        variant="Primary"
                        isLoading={loading}
                        disabled={loading}
                        onClick={handleSubmit}
                        testId={testIds.workArea.fileSharing.renameFolderDialog.saveButton}
                    >
                        {t("FileSharing.renameFolder.rename")}
                    </Button>
                </>
            }
        >
            <TextInput
                name="rename-folder-input"
                label={t("FileSharing.renameFolder.label")}
                value={newName}
                onChange={(e) => {
                    setNewName(e.target.value);
                    setValidationError(undefined);
                }}
                testIds={{ input: testIds.workArea.fileSharing.renameFolderDialog.input }}
                disabled={loading}
                error={validationError ?? renameError}
            />
        </Modal>
    );
};

export default RenameFolderModal;
